import Link from 'next/link';
import React, { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { RouteE } from 'src/constants/pages.constants';
import {
    ISectionConfiguration,
    WizardStateBanner,
} from 'src/features/common/universe-wizard/context/wizard-context.types';
import { useImageUrlCheck } from 'src/hooks/useImageUrlCheck';
import { useScreenDetect } from 'src/hooks/useScreenDetect';
import { Button } from 'src/ui/common/atoms/button';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { canvasAnimationSpeed } from 'src/ui/common/config/animations';
import { useGradientAnimation } from 'src/ui/common/hooks/useGradientAnimation.hook';
import { CountdownTimer } from 'src/ui/white-labels/nftsuperlab/atoms/countdown';
import { getUrlWithPublicStorage } from 'src/utils/get-url-with-public-host';
import {
    StyledCanvas,
    StyledDescription,
    StyledInfoBlock,
    StyledTopBanner,
    StyledWrapper,
} from './universe-top-banner.styles';

const EXPIRED_DATE = 1680285600000; /* 31/3, 19.00 GMT+1 */

export const UniverseTopBannerContainer = ({ title, description, image }: ISectionConfiguration<WizardStateBanner>) => {
    const screenType = useScreenDetect();
    const { ref: canvasRef, entry: canvas, inView } = useInView();
    const { startAnimation, stopAnimation } = useGradientAnimation();
    const bannerImage = useImageUrlCheck({ imageUrl: getUrlWithPublicStorage(image) });
    const [isCountDownEnd, setCountDownEnd] = useState(false);

    const titleFont = useMemo(() => {
        if (screenType.xs) {
            return 'md';
        }
        if (screenType.sm) {
            return 'lg';
        }
        return 'xl';
    }, [screenType]);

    useEffect(() => {
        if (inView && !bannerImage) {
            startAnimation(canvas!.target as HTMLCanvasElement, canvasAnimationSpeed.slow);
        }
        if (!inView && !bannerImage) {
            stopAnimation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView, canvas, bannerImage]);

    return (
        <StyledTopBanner img={bannerImage}>
            {!bannerImage && <StyledCanvas ref={canvasRef} width="32" height="32" />}
            <StyledWrapper>
                <StyledInfoBlock>
                    <div>
                        <Typography size={titleFont} variant="title" color="text">
                            {title}
                        </Typography>
                        <StyledDescription
                            size={screenType.xs || screenType.sm ? 'xs' : 'sm'}
                            variant="text"
                            color="gs200"
                        >
                            <span dangerouslySetInnerHTML={{ __html: description! }} />
                        </StyledDescription>
                        <StyledDescription
                            size={screenType.xs || screenType.sm ? 'xs' : 'sm'}
                            variant="text"
                            color="gs200"
                        >
                            En oldgammel fortælling om menneskets kamp imellem over-jeg’et og id’et afsendt fra
                            fremtidens teknologi i form af en unik NFT kollektion med tilhørende plakat.
                        </StyledDescription>
                    </div>
                    <StyledDescription size={screenType.xs || screenType.sm ? 'xs' : 'sm'} variant="text" color="gs200">
                        Salget er lukket og det er derfor kun muligt at købe en NFT fra det sekundære marked.
                    </StyledDescription>
                    {!isCountDownEnd && <CountdownTimer targetDate={EXPIRED_DATE} setCountDownEnd={setCountDownEnd} />}
                    <Spacing mt="15px" mtMd="24px">
                        <Link href={RouteE.EXPLORE} prefetch={false}>
                            <Button
                                variant="white"
                                size={screenType.xs || screenType.sm ? 'md' : 'lg'}
                                disabled={!isCountDownEnd}
                            >
                                Køb nu
                            </Button>
                        </Link>
                    </Spacing>
                </StyledInfoBlock>
            </StyledWrapper>
        </StyledTopBanner>
    );
};
